import { api } from '@shared/api';
import type { AnalyticsPeriod } from './adminAnalyticsApi';

/** Блок «Истории» в аналитике (2026-10-22): игра «Выживи в Лондоне» — воронка, перепрохождения, расходы. */
export interface AdminStories {
  days: AnalyticsPeriod;
  openedUsers: number;
  startedUsers: number;
  runs: number;
  finishedUsers: number;
  finishedRuns: number;
  replayUsers: number;
  avgTurns: number | null;
  turns: number;
  voiceTurns: number;
  fallbackTurns: number;
  endings: { id: string; count: number }[];
  droppedAt: { turn: number; count: number }[];
  interest: { yes: number; like: number };
  llmTokens: { input: number; output: number };
  ttsChars: number;
  daily: { users: number; runs: number; done: number };
  boss: { users: number; fights: number; wins: number; resolvedMistakes: number };
  demo: AdminStoriesDemo;
}

/** Демо миссии без регистрации (/demo, 2026-10-25): по дням МСК, сбои ведущего не считаются. */
export interface AdminStoriesDemo {
  limit: number;
  perIp: number;
  maxTurns: number;
  today: number;
  started: number;
  finished: number;
  done: number;
  voice: number;
  days: { day: string; started: number; finished: number; done: number; voice: number; avgTurns: number | null }[];
  llmTokens: { input: number; output: number };
}

interface DemoRaw {
  limit: number;
  per_ip: number;
  max_turns: number;
  today: number;
  started: number;
  finished: number;
  done: number;
  voice: number;
  days?: { day: string; started: number; finished: number; done: number; voice: number; avg_turns: number | null }[];
  llm_tokens?: { input: number; output: number };
}

export interface AdminElevenlabs {
  /** false — ключ не задан: реплики без озвучки. */
  configured: boolean;
  tier: string;
  used: number;
  limit: number;
  left: number;
  resetsAt: string | null;
  error: string | null;
  days: { day: string; chars: number }[];
}

interface StoriesRaw {
  days: AnalyticsPeriod;
  opened_users: number;
  started_users: number;
  runs: number;
  finished_users: number;
  finished_runs: number;
  replay_users: number;
  avg_turns: number | null;
  turns: number;
  voice_turns: number;
  fallback_turns: number;
  endings?: { id: string; count: number }[];
  dropped_at?: { turn: number; count: number }[];
  interest?: { yes: number; like: number };
  llm_tokens?: { input: number; output: number };
  tts_chars: number;
  daily?: { users: number; runs: number; done: number };
  boss?: { users: number; fights: number; wins: number; resolved_mistakes: number };
  demo?: DemoRaw;
}

function demoFrom(raw: DemoRaw | undefined): AdminStoriesDemo {
  if (!raw) return { limit: 0, perIp: 0, maxTurns: 0, today: 0, started: 0, finished: 0, done: 0, voice: 0, days: [], llmTokens: { input: 0, output: 0 } };
  return {
    limit: raw.limit,
    perIp: raw.per_ip,
    maxTurns: raw.max_turns,
    today: raw.today,
    started: raw.started,
    finished: raw.finished,
    done: raw.done,
    voice: raw.voice,
    days: (raw.days ?? []).map((d) => ({ day: d.day, started: d.started, finished: d.finished, done: d.done, voice: d.voice, avgTurns: d.avg_turns })),
    llmTokens: raw.llm_tokens ?? { input: 0, output: 0 },
  };
}

/** GET /api/v1/admin/stories?days= */
export async function fetchAdminStories(days: AnalyticsPeriod): Promise<AdminStories> {
  const { data } = await api.get<StoriesRaw>('/admin/stories', { params: { days } });
  return {
    days: data.days,
    openedUsers: data.opened_users ?? 0,
    startedUsers: data.started_users ?? 0,
    runs: data.runs ?? 0,
    finishedUsers: data.finished_users ?? 0,
    finishedRuns: data.finished_runs ?? 0,
    replayUsers: data.replay_users ?? 0,
    avgTurns: data.avg_turns ?? null,
    turns: data.turns ?? 0,
    voiceTurns: data.voice_turns ?? 0,
    fallbackTurns: data.fallback_turns ?? 0,
    endings: data.endings ?? [],
    droppedAt: data.dropped_at ?? [],
    interest: data.interest ?? { yes: 0, like: 0 },
    llmTokens: data.llm_tokens ?? { input: 0, output: 0 },
    ttsChars: data.tts_chars ?? 0,
    daily: data.daily ?? { users: 0, runs: 0, done: 0 },
    boss: data.boss
      ? { users: data.boss.users, fights: data.boss.fights, wins: data.boss.wins, resolvedMistakes: data.boss.resolved_mistakes }
      : { users: 0, fights: 0, wins: 0, resolvedMistakes: 0 },
    demo: demoFrom(data.demo),
  };
}

/** GET /api/v1/admin/elevenlabs — кредиты на учётке (кэш 10 минут) и озвученные символы за 7 дней. */
export async function fetchAdminElevenlabs(): Promise<AdminElevenlabs> {
  const { data } = await api.get<{
    configured: boolean;
    tier?: string;
    used?: number;
    limit?: number;
    left?: number;
    resets_at?: string | null;
    error?: string | null;
    days?: { day: string; chars: number }[];
  }>('/admin/elevenlabs');
  return {
    configured: !!data.configured,
    tier: data.tier ?? '',
    used: data.used ?? 0,
    limit: data.limit ?? 0,
    left: data.left ?? 0,
    resetsAt: data.resets_at ?? null,
    error: data.error ?? null,
    days: data.days ?? [],
  };
}
